import type React from 'react';
import {useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField} from '@mui/material';
import type {RiskDraft, RiskInput} from '../../types/categoryRecordTypes';

type Props = {
    title: string;
    initial: RiskDraft & {status: string};
    levels: string[];
    statuses: string[];
    onSubmit: (input: RiskInput) => Promise<unknown>;
    onClose: () => void;
};

/** Add or edit a category risk: title, description, likelihood, impact, mitigation and status. Mount it to open it. */
export const RiskDialog: React.FC<Props> = ({title, initial, levels, statuses, onSubmit, onClose}) => {
    const [input, setInput] = useState(initial);
    const [busy, setBusy] = useState(false);
    const set = (k: keyof typeof input) => (e: React.ChangeEvent<HTMLInputElement>) => setInput({...input, [k]: e.target.value});
    const submit = () => {
        setBusy(true);
        onSubmit({...input, title: input.title.trim(), description: input.description.trim(), mitigation: input.mitigation.trim()})
            .then(onClose)
            .catch(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cp-risk-title">
            <DialogTitle id="cp-risk-title">{title}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    <TextField label="Risk" required autoFocus size="small" value={input.title} onChange={set('title')}
                               slotProps={{htmlInput: {maxLength: 200}}}/>
                    <TextField label="Description" multiline minRows={2} size="small" value={input.description}
                               onChange={set('description')}/>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        {(['likelihood', 'impact'] as const).map(k => (
                            <TextField key={k} select fullWidth size="small" label={k === 'likelihood' ? 'Likelihood' : 'Impact'}
                                       value={input[k]} onChange={set(k)}>
                                {levels.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                            </TextField>
                        ))}
                        <TextField select fullWidth size="small" label="Status" value={input.status} onChange={set('status')}>
                            {statuses.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                        </TextField>
                    </Stack>
                    <TextField label="Mitigation" multiline minRows={2} size="small" value={input.mitigation}
                               onChange={set('mitigation')}/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !input.title.trim()}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};
